import { orderBy, partition } from 'lodash'

import { Layer } from 'almanac-ui/icons/24/Layer'
import { ReactComponent as DocIcon16 } from './icons/document16.svg'
import { ReactComponent as DocIcon24 } from './icons/document24.svg'
import { ReactComponent as FolderIcon16 } from './icons/folder16.svg'
import { ReactComponent as FolderIcon24 } from './icons/folder24.svg'
import { ChannelWithPermission, InviteWithPermission, UserWithPermission } from './Types'
import { Access, ChannelInfo, FileAccessGrant, RecipientsSelectorValueType, ShareableFileType, ShareUser } from 'types'

import Styles from './ShareSheet.module.scss'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'

export const isChannelWithPermission = (x: any): x is ChannelWithPermission => {
  return !!x.name && !!x.permission
}

export const isShareUser = (entity: any): entity is ShareUser => {
  return !!entity.first_name && !!entity.last_name && !!entity.email
}

export const isUserWithPermission = (entity: any): entity is UserWithPermission => {
  return !!entity.first_name && !!entity.email && !!entity.permission
}

export const isInviteWithPermission = (entity: any): entity is InviteWithPermission => {
  return !isUserWithPermission(entity) && !isChannelWithPermission(entity)
}

export const isCompany = (x: any): boolean => {
  if (!x) return false

  return x.accessor_type === 'Company'
}

export const isChannel = (x: any): boolean => {
  if (!x) return false

  return !!x.name && !x.accessor_type
}

export const isChannelInfo = (x: any): x is ChannelInfo => {
  return !!x.name
}

export const getIcon = (
  fileType: ShareableFileType,
  fileIcon?: string | null | undefined,
  large?: boolean,
  channel?: ChannelType | null,
) => {
  if (fileType === 'layer') return <Layer color={Styles.grey50} />

  if (!!channel) {
    return <ChannelIcon channel={channel} size={!!large ? 24 : 16} />
  }

  if (!!fileIcon) return fileIcon

  if (!!large) {
    return fileType === 'folder' ? <FolderIcon24 /> : <DocIcon24 />
  }

  return fileType === 'folder' ? <FolderIcon16 /> : <DocIcon16 />
}

export const orderAccesses = (accesses: FileAccessGrant[]) => {
  if (!accesses.length) return accesses

  const [companyAccess, nonCompanyAccesses] = partition(accesses, ({ accessor }) => accessor.type === 'Company')

  const [currentUserAccesses, filteredAccesses] = partition(
    nonCompanyAccesses,
    ({ accessor }) => accessor.is_current_user,
  )

  const [layerEditors, layerFilteredAccesses] = partition(
    filteredAccesses,
    ({ accessor }) => accessor.type === RecipientsSelectorValueType.LayerEditors,
  )

  const orderedAccesses = orderBy(
    layerFilteredAccesses,
    ['access_number', (access) => access.accessor.name.toLowerCase()],
    ['desc', 'asc'],
  )

  return [companyAccess[0], currentUserAccesses[0], layerEditors[0], ...orderedAccesses].filter((a) => a)
}

export const canEdit = (accessLevel: Access | undefined) => {
  return ['edit', 'owner'].includes(accessLevel || '')
}
